import React, { useMemo } from "react";
import type { Transaction } from "../types";

interface StatsStripProps {
  transactions: Transaction[];
}

function formatAmount(value: number, withSign = false): string {
  const sign = withSign && value > 0 ? "+" : "";
  const abs = Math.abs(value);
  return sign + new Intl.NumberFormat("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value < 0 ? -abs : abs);
}

/**
 * 4 hairline-divider columns, no boxed card chrome.
 * Mobile: 2x2 grid with internal hairlines. Desktop: 4 columns in a row.
 */
export function StatsStrip({ transactions }: StatsStripProps) {
  const stats = useMemo(() => {
    const moneyIn = transactions.filter((t) => t.direction === "in").reduce((s, t) => s + Math.abs(t.amount), 0);
    const moneyOut = transactions.filter((t) => t.direction === "out").reduce((s, t) => s + Math.abs(t.amount), 0);
    const net = moneyIn - moneyOut;
    const balance = net; // Simplification; real balance comes from the account hook.
    return { balance, moneyIn, moneyOut, net };
  }, [transactions]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 py-2.5 mb-5 border-t border-b border-lineSoft gap-y-2.5">
      <div className="px-4 md:border-r md:border-lineSoft first:pl-0">
        <p className="text-[10.5px] uppercase tracking-wide font-semibold text-ink3 mb-0.5">Current balance</p>
        <p className="text-base md:text-lg font-bold text-ink tabular-nums -tracking-[0.3px]">
          {formatAmount(stats.balance)}<span className="text-[11px] text-ink4 font-medium ml-1">CAD</span>
        </p>
        <p className="text-[11px] text-ink3">As of today</p>
      </div>
      <div className="px-4 md:border-r md:border-lineSoft">
        <p className="text-[10.5px] uppercase tracking-wide font-semibold text-ink3 mb-0.5">Money in</p>
        <p className="text-base md:text-lg font-bold text-teal tabular-nums">+{formatAmount(stats.moneyIn)}</p>
        <p className="text-[11px] text-ink3">This month</p>
      </div>
      <div className="px-4 md:border-r md:border-lineSoft first:pl-0 md:first:pl-4">
        <p className="text-[10.5px] uppercase tracking-wide font-semibold text-ink3 mb-0.5">Money out</p>
        <p className="text-base md:text-lg font-bold text-ink tabular-nums">-{formatAmount(stats.moneyOut)}</p>
        <p className="text-[11px] text-ink3">This month</p>
      </div>
      <div className="px-4 md:last:pr-0">
        <p className="text-[10.5px] uppercase tracking-wide font-semibold text-ink3 mb-0.5">Net this month</p>
        <p className={`text-base md:text-lg font-bold tabular-nums ${stats.net >= 0 ? "text-teal" : "text-ink"}`}>
          {stats.net >= 0 ? "+" : ""}{formatAmount(stats.net)}
        </p>
        <p className="text-[11px] text-ink3">After expenses</p>
      </div>
    </div>
  );
}